import React, { useState } from "react";
import { FileText, Trash2, ChevronDown, ChevronRight, Layers, RotateCcw } from "lucide-react";

const Toggle = ({ d, isOpen, setExpandedDoc }) => (
  <button onClick={() => setExpandedDoc(isOpen ? null : d.id)} className="text-slate-500 hover:text-amber-400" data-testid={`doc-sections-toggle-${d.id}`}>
    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
  </button>
);
const DeleteCell = ({ d, can, label, onDelete }) => can("edit") && (
  <td className="text-right"><button onClick={() => onDelete({ type: "document", id: d.id, label })} className="text-slate-500 hover:text-red-400" data-testid={`delete-doc-${d.id}`}><Trash2 className="w-4 h-4" /></button></td>
);
const Detail = ({ d, cols, children }) => (
  <tr><td colSpan={8} className="bg-slate-950/50 border-t border-slate-800"><div className={`p-3 grid ${cols} gap-x-6 gap-y-1 text-xs font-mono`} data-testid={`doc-sections-detail-${d.id}`}>{children}</div></td></tr>
);

function DrawingRow({ d, can, isOpen, setExpandedDoc, onDelete }) {
  const di = d.drawing_index;
  return (
    <React.Fragment>
      <tr data-testid={`doc-row-${d.id}`}>
        <td className="w-6"><Toggle d={d} isOpen={isOpen} setExpandedDoc={setExpandedDoc} /></td>
        <td className="text-slate-100"><Layers className="w-3.5 h-3.5 inline mr-1.5 text-sky-400" />{d.name}</td>
        <td className="capitalize">Drawings</td>
        <td className="font-mono text-xs">—</td>
        <td className="text-xs font-mono" data-testid={`doc-sections-summary-${d.id}`}>
          <span className="text-sky-400">{di.structural_sheet_count} S-sheets</span>
          {di.notes_pages?.length > 0 && <span className="ml-2 text-emerald-400">{di.notes_pages.length} notes</span>}
        </td>
        <td className="font-mono">{d.page_count}</td>
        <td className="font-mono text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
        <DeleteCell d={d} can={can} label={`drawing set "${d.name}"`} onDelete={onDelete} />
      </tr>
      {isOpen && <Detail d={d} cols="grid-cols-3">{di.sheets.map((s) => (
        <div key={s.page} className={s.is_notes ? "text-emerald-300" : "text-slate-300"}><span className="text-sky-400">{s.sheet || "?"}</span> {s.title} <span className="text-slate-500">p.{s.page}</span>{s.is_notes && <span className="text-emerald-500/70"> · notes</span>}</div>
      ))}</Detail>}
    </React.Fragment>
  );
}

const KIND_LABEL = { narrative: "narrative", spec_revision: "spec", schedule: "schedule", attachment: "attachment", drawing: "drawing", sketch: "sketch", scanned: "scanned", scanned_ocr: "OCR", blank: "blank", unreadable: "unreadable" };
const KIND_TONE = { drawing: "text-sky-400", sketch: "text-sky-400", spec_revision: "text-amber-400", narrative: "text-slate-300", scanned: "text-orange-400", scanned_ocr: "text-emerald-400", unreadable: "text-red-400" };
const PageKinds = ({ d }) => {
  const kinds = Object.entries(d.page_kinds || {}).filter(([k]) => k !== "blank");
  if (!kinds.length) return null;
  return <div className="mt-0.5 flex flex-wrap gap-x-2 font-mono text-[10px]" data-testid={`doc-page-kinds-${d.id}`}>{kinds.map(([k, n]) => <span key={k} className={KIND_TONE[k] || "text-slate-400"}>{n} {KIND_LABEL[k] || k}</span>)}</div>;
};

const FailedStatus = ({ d, can, onRetry }) => (
  <span className="font-mono" data-testid={`doc-failed-${d.id}`}>
    <span className="text-red-400" title={d.error}>{d.interrupted ? "interrupted" : "failed"}{d.stage ? ` at ${d.stage}` : ""}</span>
    {d.error && <div className="text-[10px] text-slate-500 normal-case max-w-xs truncate" title={d.error} data-testid={`doc-error-${d.id}`}>{d.error}</div>}
    {d.recoverable && can("edit") && (
      <button onClick={(e) => { e.stopPropagation(); onRetry(d); }} className="mt-1 inline-flex items-center gap-1 text-[10px] border border-amber-500/50 hover:border-amber-400 hover:bg-amber-500/10 rounded px-1.5 py-0.5 text-amber-300" title="Original PDF is stored durably — reprocess it without uploading again" data-testid={`doc-retry-${d.id}`}>
        <RotateCcw className="w-3 h-3" /> Retry processing{d.retry_count ? ` (${d.retry_count})` : ""}
      </button>
    )}
    {!d.recoverable && <div className="text-[10px] text-slate-500 normal-case" data-testid={`doc-reupload-${d.id}`}>Original not stored — upload the PDF again</div>}
  </span>
);

const OcrRetry = ({ d, can, onRetry }) => {
  const [confirm, setConfirm] = useState(false);
  const r = d.ocr_retry;
  if (!r?.eligible || !can("edit")) return null;
  const pages = r.pages.join(", ");
  return (
    <span className="block mt-1 normal-case" data-testid={`doc-ocr-retry-${d.id}`}>
      {!confirm ? (
        <button onClick={(e) => { e.stopPropagation(); setConfirm(true); }} className="inline-flex items-center gap-1 text-[10px] border border-amber-500/50 hover:border-amber-400 hover:bg-amber-500/10 rounded px-1.5 py-0.5 text-amber-300" title={`OCR failed on page(s) ${pages}; their content is excluded from AI Review`} data-testid={`doc-ocr-retry-btn-${d.id}`}>
          <RotateCcw className="w-3 h-3" /> Retry OCR (reprocess from stored original)
        </button>
      ) : (
        <span className="block text-[10px] text-slate-300 border border-amber-500/40 rounded p-2 max-w-sm" onClick={(e) => e.stopPropagation()} data-testid={`doc-ocr-retry-confirm-${d.id}`}>
          Re-runs extraction and OCR for this whole document from the stored original under the current OCR time budget. Page(s) {pages} are currently missing from the review context. Nothing is deleted or re-uploaded; the document keeps its ID, history and does not count as a new upload.
          <span className="flex gap-2 mt-1.5">
            <button onClick={() => { setConfirm(false); onRetry(d); }} className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded px-2 py-0.5" data-testid={`doc-ocr-retry-go-${d.id}`}>Reprocess now</button>
            <button onClick={() => setConfirm(false)} className="border border-slate-700 rounded px-2 py-0.5 text-slate-400" data-testid={`doc-ocr-retry-cancel-${d.id}`}>Cancel</button>
          </span>
        </span>
      )}
    </span>
  );
};

const SpecStatus = ({ d, steel, missing, can, onRetry }) => {
  if (d.status === "processing") return <span className="text-amber-400 font-mono animate-pulse" data-testid={`doc-processing-${d.id}`}>⏳ {d.progress || "Processing…"}</span>;
  if (d.status === "failed") return <FailedStatus d={d} can={can} onRetry={onRetry} />;
  const warn = d.warnings?.length > 0;
  return <>{steel.length ? <span className="text-emerald-400 font-mono">{steel.length} found</span> : <span className="text-slate-500">—</span>}
    {missing.length > 0 && <span className="ml-2 text-red-400 font-mono">{missing.length} in TOC only</span>}
    {warn && <span className="ml-2 text-orange-400 font-mono" title={d.warnings.join("\n")} data-testid={`doc-warnings-${d.id}`}>⚠ {d.warnings.length} warning{d.warnings.length > 1 ? "s" : ""}</span>}
    <PageKinds d={d} />
    <OcrRetry d={d} can={can} onRetry={onRetry} /></>;
};

function SpecDetail({ d, steel, missing, sheets }) {
  return (
    <Detail d={d} cols="grid-cols-2">

        {steel.map((s) => <div key={s.code} className="text-slate-300"><span className="text-amber-400">{s.code}</span> {s.title} <span className="text-slate-500">pp. {s.page_start}–{s.page_end}</span></div>)}
        {missing.map((m) => <div key={m.code} className="text-red-400"><span>{m.code}</span> {m.title} <span className="text-red-500/70">— in TOC, body not found</span></div>)}
        {(d.warnings || []).map((w) => <div key={w} className="text-orange-300 col-span-2" data-testid={`doc-warning-line-${d.id}`}>⚠ {w}</div>)}
        {sheets.map((s) => <div key={s.page} className={s.is_notes ? "text-emerald-300" : "text-slate-300"}><span className="text-sky-400">{s.sheet || "sheet ?"}</span> {s.title} <span className="text-slate-500">p.{s.page}</span>{s.is_notes && <span className="text-emerald-500/70"> · steel notes</span>}</div>)}
    </Detail>
  );
}

function SpecRow({ d, can, isOpen, setExpandedDoc, onDelete, onRetry }) {
  const si = d.section_index || {};
  const steel = (si.sections || []).filter((s) => s.code.startsWith("05") || s.code.startsWith("10"));
  const missing = si.toc_missing_body || [];
  const sheets = d.addendum_drawings?.sheets || [];
  const hasDetail = steel.length > 0 || missing.length > 0 || sheets.length > 0 || (d.warnings || []).length > 0;
  return (
    <React.Fragment>
      <tr data-testid={`doc-row-${d.id}`}>
        <td className="w-6">{hasDetail && <Toggle d={d} isOpen={isOpen} setExpandedDoc={setExpandedDoc} />}</td>
        <td className="text-slate-100"><FileText className="w-3.5 h-3.5 inline mr-1.5 text-slate-500" />{d.name}</td>
        <td className="capitalize">{d.doc_type}</td>
        <td className="font-mono text-xs">{d.csi_division || "—"}</td>
        <td className="text-xs" data-testid={`doc-sections-summary-${d.id}`}><SpecStatus d={d} steel={steel} missing={missing} can={can} onRetry={onRetry} /></td>
        <td className="font-mono">{d.status === "processing" ? "…" : d.page_count}</td>
        <td className="font-mono text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString()}</td>
        <DeleteCell d={d} can={can} label={`document "${d.name}"`} onDelete={onDelete} />
      </tr>
      {isOpen && <SpecDetail d={d} steel={steel} missing={missing} sheets={sheets} />}
    </React.Fragment>
  );
}

export function DocumentsTable({ docs, can, expandedDoc, setExpandedDoc, onDelete, onRetry }) {
  return (
    <div className="overflow-hidden">
      <table className="sc-table w-full">
        <thead><tr><th></th><th>Name</th><th>Type</th><th>CSI</th><th>Div 05/10 Sections / Sheets</th><th>Pages</th><th>Added</th>{can("edit") && <th></th>}</tr></thead>
        <tbody>
          {docs.map((d) => { const Row = d.drawing_index ? DrawingRow : SpecRow; return <Row key={d.id} d={d} can={can} isOpen={expandedDoc === d.id} setExpandedDoc={setExpandedDoc} onDelete={onDelete} onRetry={onRetry} />; })}
          {!docs.length && (<tr><td colSpan={8} className="text-center py-8 text-slate-500">No documents. Upload a PDF contract or spec to begin.</td></tr>)}
        </tbody>
      </table>
    </div>
  );
}
